import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import UIEventBus from '../EventBus';

interface ProjectsModalProps {
    visible: boolean;
    onClose: () => void;
}

const PROJECTS = [
    {
        title: 'AssetLens 2.0',
        description: 'Advanced real estate and geospatial analysis platform with AI-powered dictation, multi-tenant authentication, and detailed legal templates.',
        tags: ['Next.js', 'FastAPI', 'PostgreSQL', 'TailwindCSS'],
        link: 'https://github.com/DennisNzioki/assetlens2.0'
    },
    {
        title: 'DennisNzioki.com OS',
        description: 'The web desktop Operating System environment running in the monitor of this 3D portfolio. Fully interactive terminal, browser, and apps.',
        tags: ['React', 'TypeScript', 'Webpack', 'TailwindCSS'],
        link: 'https://mywebsite-os.vercel.app/'
    },
    {
        title: 'KOT FM',
        description: 'Digital audio stream and entertainment hub featuring live shows, podcast integration, and schedule tracking.',
        tags: ['React', 'Node.js', 'Express', 'Spotify API'],
        link: 'https://github.com/DennisNzioki'
    }
];

const ProjectsModal: React.FC<ProjectsModalProps> = ({ visible, onClose }) => {
    return (
        <AnimatePresence>
            {visible && (
                <div style={styles.backdrop}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        style={styles.modal}
                        className="projects-modal-container"
                    >
                        {/* Title Bar */}
                        <div style={styles.titleBar}>
                            <p style={styles.titleText}>PROJECT_CARDS.EXE</p>
                            <button 
                                onClick={onClose} 
                                style={styles.closeButton}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ff5f56';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#fff';
                                }}
                            >
                                X
                            </button>
                        </div>

                        {/* Content */}
                        <div style={styles.content}>
                            <p style={styles.subtitle}>Select a card to view repository / demo:</p>
                            <div style={styles.grid}>
                                {PROJECTS.map((project, index) => (
                                    <motion.div
                                        key={project.title}
                                        whileHover={{ y: -4, borderColor: '#00ff00', boxShadow: '0 0 15px rgba(0, 255, 0, 0.2)' }}
                                        style={styles.card}
                                    >
                                        <h3 style={styles.projectTitle}>{project.title}</h3>
                                        <p style={styles.projectDesc}>{project.description}</p>
                                        <div style={styles.tagContainer}>
                                            {project.tags.map(t => (
                                                <span key={t} style={styles.tag}>{t}</span>
                                            ))}
                                        </div>
                                        <a 
                                            href={project.link} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            style={styles.launchBtn}
                                            onClick={() => {
                                                UIEventBus.dispatch('muteToggle', false); // Play keyboard sound
                                            }}
                                        >
                                            LAUNCH_CARD &gt;
                                        </a>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    backdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 999999,
        pointerEvents: 'auto'
    },
    modal: {
        width: '90%',
        maxWidth: '850px',
        backgroundColor: 'rgba(10, 15, 10, 0.95)',
        border: '3px solid #00ff00',
        boxShadow: '0 0 20px rgba(0, 255, 0, 0.3)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
    },
    titleBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid #00ff00',
        padding: '8px 16px',
        backgroundColor: 'rgba(0, 255, 0, 0.1)',
    },
    titleText: {
        margin: 0,
        fontWeight: 'bold',
        color: '#00ff00',
        letterSpacing: '1px',
    },
    closeButton: {
        background: 'transparent',
        border: '1px solid #00ff00',
        color: '#00ff00',
        cursor: 'pointer',
        padding: '2px 8px',
        fontSize: '14px',
        fontFamily: 'monospace',
        transition: 'all 0.2s',
    },
    content: {
        padding: '24px',
        maxHeight: '70vh',
        overflowY: 'auto',
    },
    subtitle: {
        color: '#888',
        marginBottom: '20px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: '20px',
    },
    card: {
        border: '1px solid #333',
        padding: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
    },
    projectTitle: {
        margin: '0 0 10px 0',
        color: '#00ff00',
        fontSize: '18px',
    },
    projectDesc: {
        fontSize: '13px',
        color: '#ccc',
        lineHeight: '1.4',
        marginBottom: '16px',
    },
    tagContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '16px',
    },
    tag: {
        fontSize: '10px',
        border: '1px solid #444',
        padding: '2px 6px',
        backgroundColor: '#111',
        color: '#aaa',
    },
    launchBtn: {
        display: 'block',
        textAlign: 'center',
        border: '1px solid #00ff00',
        color: '#00ff00',
        padding: '8px',
        fontSize: '12px',
        textDecoration: 'none',
        transition: 'all 0.2s',
        backgroundColor: 'rgba(0, 255, 0, 0.05)',
        fontWeight: 'bold',
        cursor: 'pointer',
    }
};

export default ProjectsModal;
